import { AudioAnalyzer, AnalysisData } from './AudioAnalyzer';

export class AudioManager {
  private audioContext: AudioContext | null = null;
  private musicGainNode: GainNode | null = null;
  private voiceoverGainNode: GainNode | null = null;
  private analyzerNode: AnalyserNode | null = null;
  private masterCompressor: DynamicsCompressorNode | null = null;
  private masterGain: GainNode | null = null;
  private reverbNode: ConvolverNode | null = null;
  private reverbGain: GainNode | null = null;
  private dryGain: GainNode | null = null;
  
  private currentMusicAudio: HTMLAudioElement | null = null;
  private nextMusicAudio: HTMLAudioElement | null = null;
  private voiceoverAudio: HTMLAudioElement | null = null;
  
  private currentMusicSource: MediaElementAudioSourceNode | null = null;
  private nextMusicSource: MediaElementAudioSourceNode | null = null;
  private voiceoverSource: MediaElementAudioSourceNode | null = null;
  
  private audioAnalyzer: AudioAnalyzer | null = null;
  private crossfadeInterval: number | null = null;
  
  private voiceoverEndCallback: (() => void) | null = null;
  
  // Track if music has been connected (can only connect once)
  private musicSourceConnected: boolean = false;
  
  // iOS detection
  private isIOS: boolean = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  
  public async init(): Promise<void> {
    // Cross-browser AudioContext (Safari needs webkit prefix)
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    
    if (!AudioContextClass) {
      console.error('Web Audio API not supported');
      return;
    }
    
    this.audioContext = new AudioContextClass();
    
    // iOS/Safari specific: must resume on user interaction
    // Try multiple times as iOS can be stubborn
    for (let i = 0; i < 3; i++) {
      if (this.audioContext.state === 'suspended') {
        try {
          await this.audioContext.resume();
          await new Promise(r => setTimeout(r, 100));
        } catch (e) {
          console.log(`Audio context resume attempt ${i + 1} failed:`, e);
        }
      }
    }
    
    // iOS audio unlock - play silent buffer to unlock audio
    await this.unlockAudioContext();
    
    console.log('AudioContext state after init:', this.audioContext.state);
    
    // Create master chain for normalization
    this.masterGain = this.audioContext.createGain();
    this.masterCompressor = this.audioContext.createDynamicsCompressor();
    
    // Configure compressor for gentle normalization
    this.masterCompressor.threshold.value = -24;
    this.masterCompressor.knee.value = 12;
    this.masterCompressor.ratio.value = 4;
    this.masterCompressor.attack.value = 0.003;
    this.masterCompressor.release.value = 0.25;
    
    // Create deep reverb
    this.reverbNode = this.audioContext.createConvolver();
    this.reverbGain = this.audioContext.createGain();
    this.dryGain = this.audioContext.createGain();
    
    // Generate deep reverb impulse response
    this.reverbNode.buffer = this.createReverbImpulse(4.0, 3.5); // 4s duration, deep decay
    
    // Reverb mix levels
    this.dryGain.gain.value = 0.75;   // Dry signal
    this.reverbGain.gain.value = 0.4; // Wet reverb (deep but not overwhelming)
    
    // Create gain nodes
    this.musicGainNode = this.audioContext.createGain();
    this.voiceoverGainNode = this.audioContext.createGain();
    this.analyzerNode = this.audioContext.createAnalyser();
    
    // Configure analyzer
    this.analyzerNode.fftSize = 256;
    this.analyzerNode.smoothingTimeConstant = 0.8;
    
    // Connect audio graph with reverb:
    // Music -> musicGain -> analyzer -> masterGain -> [dry + reverb] -> compressor -> destination
    this.musicGainNode.connect(this.analyzerNode);
    this.analyzerNode.connect(this.masterGain);
    this.voiceoverGainNode.connect(this.masterGain);
    
    // Split to dry and wet paths
    this.masterGain.connect(this.dryGain);
    this.masterGain.connect(this.reverbNode);
    this.reverbNode.connect(this.reverbGain);
    
    // Merge dry and wet to compressor
    this.dryGain.connect(this.masterCompressor);
    this.reverbGain.connect(this.masterCompressor);
    this.masterCompressor.connect(this.audioContext.destination);
    
    // Initialize audio analyzer
    this.audioAnalyzer = new AudioAnalyzer(this.analyzerNode);
    
    // Set initial volumes
    this.musicGainNode.gain.value = 0.5;
    this.voiceoverGainNode.gain.value = 0.85;
    this.masterGain.gain.value = 1.0;
  }
  
  // Generate impulse response for deep reverb
  private createReverbImpulse(duration: number, decay: number): AudioBuffer {
    const sampleRate = this.audioContext!.sampleRate;
    const length = sampleRate * duration;
    const impulse = this.audioContext!.createBuffer(2, length, sampleRate);
    
    for (let channel = 0; channel < 2; channel++) {
      const channelData = impulse.getChannelData(channel);
      
      for (let i = 0; i < length; i++) {
        const t = i / sampleRate;
        
        // Exponential decay with some early reflections
        const envelope = Math.exp(-t * decay);
        
        // Add some diffusion/randomness for natural sound
        const noise = (Math.random() * 2 - 1);
        
        // Early reflections (first 100ms)
        const earlyReflections = t < 0.1 ? Math.sin(t * 100) * 0.3 : 0;
        
        // Combine for deep, cathedral-like reverb
        channelData[i] = (noise + earlyReflections) * envelope;
      }
    }
    
    return impulse;
  }
  
  private createAudioElement(): HTMLAudioElement {
    const audio = document.createElement('audio');
    audio.crossOrigin = 'anonymous';
    audio.preload = 'auto';
    audio.setAttribute('playsinline', 'true');
    audio.setAttribute('webkit-playsinline', 'true');
    // iOS needs these attributes
    (audio as any).playsInline = true;
    (audio as any).webkitPlaysInline = true;
    
    // Append to DOM for better iOS compatibility
    audio.style.display = 'none';
    document.body.appendChild(audio);
    
    return audio;
  }
  
  private async loadAudioElement(audio: HTMLAudioElement, src: string): Promise<void> {
    return new Promise((resolve) => {
      let resolved = false;
      const timeout = this.isIOS ? 5000 : 3000;
      
      const onReady = () => {
        if (resolved) return;
        resolved = true;
        console.log('Audio loaded:', src);
        resolve();
      };
      
      const onError = (e: Event) => {
        if (resolved) return;
        resolved = true;
        console.error('Audio load error for', src, ':', e);
        // Still resolve to not block the flow
        resolve();
      };
      
      audio.addEventListener('canplaythrough', onReady, { once: true });
      audio.addEventListener('canplay', onReady, { once: true });
      audio.addEventListener('loadeddata', () => setTimeout(onReady, 100), { once: true });
      audio.addEventListener('error', onError, { once: true });
      
      // Fallback timeout - always resolve
      setTimeout(() => {
        if (!resolved) {
          console.log('Audio load timeout for', src, '- continuing anyway');
          onReady();
        }
      }, timeout);
      
      audio.src = src;
      audio.load();
    });
  }
  
  public async loadMusic(path: string): Promise<void> {
    if (!this.audioContext) return;
    
    // Resume context if needed (iOS)
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
    
    // If we have current music, prepare to crossfade
    if (this.currentMusicAudio && this.musicSourceConnected) {
      this.nextMusicAudio = this.createAudioElement();
      this.nextMusicAudio.loop = true;
      await this.loadAudioElement(this.nextMusicAudio, path);
      
      try {
        this.nextMusicSource = this.audioContext.createMediaElementSource(this.nextMusicAudio);
      } catch (e) {
        console.log('MediaElementSource error (may already exist):', e);
      }
    } else {
      // First time loading music
      if (!this.currentMusicAudio) {
        this.currentMusicAudio = this.createAudioElement();
      }
      this.currentMusicAudio.loop = true;
      await this.loadAudioElement(this.currentMusicAudio, path);
      
      if (!this.musicSourceConnected) {
        try {
          this.currentMusicSource = this.audioContext.createMediaElementSource(this.currentMusicAudio);
          this.currentMusicSource.connect(this.musicGainNode!);
          this.musicSourceConnected = true;
        } catch (e) {
          console.log('MediaElementSource error:', e);
        }
      }
    }
  }
  
  public async playMusic(startTime: number = 0, fadeIn: boolean = false): Promise<void> {
    if (!this.currentMusicAudio || !this.musicGainNode) {
      console.error('Music not ready');
      return;
    }
    
    console.log('playMusic called, context state:', this.audioContext?.state);
    
    // Resume audio context if needed (iOS) - MUST await
    if (this.audioContext?.state === 'suspended') {
      try {
        await this.audioContext.resume();
        console.log('Audio context resumed for music');
      } catch (e) {
        console.error('Failed to resume audio context for music:', e);
      }
    }
    
    this.currentMusicAudio.currentTime = startTime;
    
    const startPlayback = async () => {
      const maxAttempts = this.isIOS ? 5 : 3;
      
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          console.log(`Music play attempt ${attempt}`);
          const playPromise = this.currentMusicAudio!.play();
          if (playPromise !== undefined) {
            await playPromise;
          }
          console.log('Music playing successfully');
          return;
        } catch (e) {
          console.warn(`Music play attempt ${attempt} failed:`, e);
          
          if (attempt < maxAttempts) {
            await new Promise(r => setTimeout(r, 100 * attempt));
            
            if (this.audioContext?.state === 'suspended') {
              await this.audioContext.resume().catch(() => {});
            }
          }
        }
      }
      console.error('All music play attempts failed');
    };
    
    if (fadeIn) {
      this.musicGainNode.gain.value = 0;
      await startPlayback();
      
      // Fade in over 3 seconds
      const startGain = 0;
      const endGain = 0.5;
      const duration = 3000;
      const startTimestamp = performance.now();
      
      const fadeStep = () => {
        const elapsed = performance.now() - startTimestamp;
        const progress = Math.min(elapsed / duration, 1);
        const eased = this.easeInOutQuad(progress);
        this.musicGainNode!.gain.value = startGain + (endGain - startGain) * eased;
        
        if (progress < 1) {
          requestAnimationFrame(fadeStep);
        }
      };
      
      requestAnimationFrame(fadeStep);
    } else {
      await startPlayback();
    }
  }
  
  public crossfadeToNextMusic(durationMs: number = 15000): void {
    if (!this.nextMusicAudio || !this.audioContext || !this.musicGainNode || !this.masterGain) return;
    
    // Create a separate gain node for the next track
    const nextGainNode = this.audioContext.createGain();
    nextGainNode.gain.value = 0;
    this.nextMusicSource?.connect(nextGainNode);
    nextGainNode.connect(this.masterGain);
    
    const startTimestamp = performance.now();
    const currentGainStart = this.musicGainNode.gain.value;
    
    // Smooth overlapping crossfade:
    // - Music starts fading IMMEDIATELY
    // - New track starts at 40% through fade-out
    // - Both overlap smoothly
    const fadeOutDuration = durationMs * 0.8;
    const fadeInDelay = durationMs * 0.35;  // New track enters earlier
    const fadeInDuration = durationMs * 0.65;
    
    let nextTrackStarted = false;
    
    const crossfadeStep = () => {
      const elapsed = performance.now() - startTimestamp;
      
      // Fade out current track IMMEDIATELY with smooth curve
      if (elapsed < fadeOutDuration) {
        const fadeOutProgress = elapsed / fadeOutDuration;
        // Smooth S-curve fade out
        const fadeOutEased = 1 - (fadeOutProgress * fadeOutProgress * (3 - 2 * fadeOutProgress));
        this.musicGainNode!.gain.value = currentGainStart * fadeOutEased;
      } else {
        this.musicGainNode!.gain.value = 0;
      }
      
      // Start new track earlier with gentle fade in
      if (elapsed >= fadeInDelay && !nextTrackStarted) {
        nextTrackStarted = true;
        this.nextMusicAudio!.currentTime = 0;
        this.nextMusicAudio!.play().catch(e => console.log('Next music play error:', e));
      }
      
      if (nextTrackStarted) {
        const fadeInElapsed = elapsed - fadeInDelay;
        const fadeInProgress = Math.min(fadeInElapsed / fadeInDuration, 1);
        // Smooth S-curve fade in
        const fadeInEased = fadeInProgress * fadeInProgress * (3 - 2 * fadeInProgress);
        nextGainNode.gain.value = 0.5 * fadeInEased;
      }
      
      if (elapsed < durationMs) {
        requestAnimationFrame(crossfadeStep);
      } else {
        // Cleanup old music
        this.currentMusicAudio?.pause();
        this.currentMusicSource?.disconnect();
        
        // Swap references
        this.currentMusicAudio = this.nextMusicAudio;
        this.currentMusicSource = this.nextMusicSource;
        this.nextMusicAudio = null;
        this.nextMusicSource = null;
        
        // Reconnect properly
        nextGainNode.disconnect();
        this.currentMusicSource?.disconnect();
        this.currentMusicSource?.connect(this.musicGainNode!);
        this.musicGainNode!.gain.value = 0.5;
      }
    };
    
    requestAnimationFrame(crossfadeStep);
  }
  
  public async loadVoiceover(path: string): Promise<void> {
    console.log('Loading voiceover:', path);
    
    // Cleanup previous voiceover completely
    if (this.voiceoverAudio) {
      this.voiceoverAudio.pause();
      this.voiceoverAudio.onended = null;
      this.voiceoverAudio.onerror = null;
      // Remove from DOM
      if (this.voiceoverAudio.parentNode) {
        this.voiceoverAudio.parentNode.removeChild(this.voiceoverAudio);
      }
      this.voiceoverAudio = null;
    }
    
    // Create fresh audio element for iOS compatibility
    // Do NOT connect to Web Audio API - use native HTML5 audio
    this.voiceoverAudio = document.createElement('audio');
    this.voiceoverAudio.crossOrigin = 'anonymous';
    this.voiceoverAudio.preload = 'auto';
    this.voiceoverAudio.setAttribute('playsinline', 'true');
    this.voiceoverAudio.setAttribute('webkit-playsinline', 'true');
    (this.voiceoverAudio as any).playsInline = true;
    (this.voiceoverAudio as any).webkitPlaysInline = true;
    
    // Set volume directly (no Web Audio connection)
    this.voiceoverAudio.volume = 0.85;
    
    // Append to body for iOS
    this.voiceoverAudio.style.display = 'none';
    document.body.appendChild(this.voiceoverAudio);
    
    // Set ended callback
    this.voiceoverAudio.onended = () => {
      console.log('Voiceover ended');
      this.voiceoverEndCallback?.();
    };
    
    this.voiceoverAudio.onerror = (e) => {
      console.error('Voiceover error:', e);
    };
    
    // Load audio
    await this.loadAudioElement(this.voiceoverAudio, path);
    console.log('Voiceover loaded successfully');
  }
  
  // iOS workaround: "unlock" an audio element by playing it silently from user gesture
  public async unlockVoiceover(): Promise<void> {
    if (!this.voiceoverAudio) return;
    
    console.log('Unlocking voiceover for iOS...');
    
    try {
      // Store current volume and mute
      const originalVolume = this.voiceoverAudio.volume;
      this.voiceoverAudio.volume = 0;
      this.voiceoverAudio.muted = true;
      
      // Play briefly to unlock
      const playPromise = this.voiceoverAudio.play();
      if (playPromise !== undefined) {
        await playPromise;
      }
      
      // Immediately pause
      this.voiceoverAudio.pause();
      this.voiceoverAudio.currentTime = 0;
      
      // Restore volume
      this.voiceoverAudio.volume = originalVolume;
      this.voiceoverAudio.muted = false;
      
      console.log('Voiceover unlocked successfully');
    } catch (e) {
      console.log('Voiceover unlock attempt (may fail, that is ok):', e);
    }
  }
  
  public async playVoiceover(): Promise<void> {
    if (!this.voiceoverAudio) {
      console.error('Voiceover audio element not ready');
      return;
    }
    
    console.log('playVoiceover called, isIOS:', this.isIOS);
    console.log('Voiceover readyState:', this.voiceoverAudio.readyState);
    console.log('Voiceover src:', this.voiceoverAudio.src);
    
    // Reset to beginning
    this.voiceoverAudio.currentTime = 0;
    
    // Ensure audio is not muted
    this.voiceoverAudio.muted = false;
    this.voiceoverAudio.volume = 0.85;
    
    // iOS/Safari specific: more attempts with longer waits
    const maxAttempts = this.isIOS ? 8 : 3;
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        console.log(`Voiceover play attempt ${attempt}/${maxAttempts}`);
        
        // iOS fix: ensure audio is ready
        if (this.voiceoverAudio.readyState < 2) {
          console.log('Waiting for audio to be ready...');
          await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => resolve(), 2000);
            this.voiceoverAudio!.oncanplay = () => {
              clearTimeout(timeout);
              resolve();
            };
            this.voiceoverAudio!.onerror = () => {
              clearTimeout(timeout);
              reject(new Error('Audio load error'));
            };
          });
        }
        
        const playPromise = this.voiceoverAudio.play();
        if (playPromise !== undefined) {
          await playPromise;
        }
        
        console.log('Voiceover playing successfully!');
        console.log('Voiceover paused:', this.voiceoverAudio.paused);
        console.log('Voiceover currentTime:', this.voiceoverAudio.currentTime);
        return;
      } catch (e: any) {
        console.warn(`Voiceover play attempt ${attempt} failed:`, e?.message || e);
        
        if (attempt < maxAttempts) {
          // Wait with increasing delay
          const delay = Math.min(100 * attempt, 500);
          await new Promise(r => setTimeout(r, delay));
          
          // iOS workaround: reload audio
          if (this.isIOS && attempt % 2 === 0) {
            console.log('iOS: Reloading audio...');
            const currentSrc = this.voiceoverAudio.src;
            this.voiceoverAudio.src = '';
            this.voiceoverAudio.load();
            await new Promise(r => setTimeout(r, 50));
            this.voiceoverAudio.src = currentSrc;
            this.voiceoverAudio.load();
            await new Promise(r => setTimeout(r, 100));
          }
        }
      }
    }
    
    console.error('All voiceover play attempts failed - trying muted autoplay fallback');
    
    // Last resort: try muted playback then unmute (some iOS versions need this)
    try {
      this.voiceoverAudio.muted = true;
      await this.voiceoverAudio.play();
      this.voiceoverAudio.muted = false;
      console.log('Muted autoplay fallback succeeded');
    } catch (e) {
      console.error('Even muted playback failed:', e);
    }
  }
  
  public onVoiceoverEnd(callback: () => void): void {
    this.voiceoverEndCallback = callback;
  }
  
  public getVoiceoverTime(): number {
    return this.voiceoverAudio?.currentTime || 0;
  }
  
  public getAnalysisData(): AnalysisData {
    return this.audioAnalyzer?.analyze() || {
      bass: 0,
      mid: 0,
      high: 0,
      average: 0
    };
  }
  
  public pause(): void {
    this.currentMusicAudio?.pause();
    this.voiceoverAudio?.pause();
  }
  
  public resume(): void {
    this.currentMusicAudio?.play();
    this.voiceoverAudio?.play();
  }
  
  public destroy(): void {
    if (this.crossfadeInterval) {
      clearInterval(this.crossfadeInterval);
    }
    this.currentMusicAudio?.pause();
    this.voiceoverAudio?.pause();
    this.audioContext?.close();
  }
  
  private easeInOutQuad(t: number): number {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }
  
  // iOS requires audio to be "unlocked" by playing something during user interaction
  private async unlockAudioContext(): Promise<void> {
    if (!this.audioContext) return;
    
    try {
      // Method 1: Create and play a silent buffer
      const buffer = this.audioContext.createBuffer(1, 1, 22050);
      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(this.audioContext.destination);
      source.start(0);
      
      // Method 2: Create a silent oscillator (another iOS workaround)
      const oscillator = this.audioContext.createOscillator();
      const silentGain = this.audioContext.createGain();
      silentGain.gain.value = 0;
      oscillator.connect(silentGain);
      silentGain.connect(this.audioContext.destination);
      oscillator.start(0);
      oscillator.stop(0.001);
      
      // Method 3: Create and touch a silent audio element
      if (this.isIOS) {
        const silentAudio = document.createElement('audio');
        silentAudio.setAttribute('playsinline', 'true');
        silentAudio.setAttribute('webkit-playsinline', 'true');
        // Data URL of a tiny silent MP3
        silentAudio.src = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYbTRF/+AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYbTRF/+AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA==';
        silentAudio.load();
        try {
          await silentAudio.play();
        } catch (e) {
          // Ignore - this is just a workaround
        }
        silentAudio.remove();
      }
    } catch (e) {
      console.log('Audio unlock partial failure:', e);
    }
    
    // Also try to resume if suspended
    if (this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
      } catch (e) {
        console.log('Audio context resume failed:', e);
      }
    }
    
    console.log('Audio context state after unlock:', this.audioContext.state);
  }
  
  // Force resume audio context - call from user interaction
  public async forceResume(): Promise<void> {
    if (!this.audioContext) return;
    
    console.log('Force resume called, current state:', this.audioContext.state);
    
    if (this.audioContext.state === 'suspended') {
      await this.unlockAudioContext();
      
      for (let i = 0; i < 3; i++) {
        try {
          await this.audioContext.resume();
          if (this.audioContext.state === 'running') break;
        } catch (e) {
          console.log('Resume attempt failed:', e);
        }
        await new Promise(r => setTimeout(r, 100));
      }
    }
    
    console.log('Audio context state after force resume:', this.audioContext.state);
  }
}

